import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
// API
import { useRequestPasswordResetMutation } from "src/services/apiQueries/password-reset";
// mantine
import { notifications } from "@mantine/notifications";
import { Text, Title, Box, Button, LoadingOverlay } from "@mantine/core";
// misc ui coponents
import { PasswordResetCard } from "@components/password-reset-card/password-reset-card";
// form
import { useForm } from "@mantine/form";
import { LoginTextInput } from "@components/Inputs";
// utils
import z from "zod";

export const PasswordResetForm = React.memo(function PasswordResetForm() {
  const { t } = useTranslation();

  const nav = useNavigate();

  const { mutate, isPending } = useRequestPasswordResetMutation({
    onError: () => {
      notifications.show({
        title: t("passwordReset.form.notifications.errorTitle"),
        message: t("passwordReset.form.notifications.error"),
        color: "red",
      });
    },
    onSuccess: async () => {
      await nav({ to: "/passwordreset/done" });
    },
  });

  const form = useForm<{ email: string }>({
    initialValues: { email: "" },
    // validation
    validateInputOnBlur: true,
    validate: {
      email: (value) => {
        const schema = z.string().email();
        const wasOk = schema.safeParse(value).success;
        return wasOk ? null : t("passwordReset.form.validation.emailInvalid");
      },
    },
  });

  return (
    <PasswordResetCard>
      <LoadingOverlay
        visible={isPending}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Title order={2} data-testid="pw-reset-form-title">
        {t("passwordReset.form.title")}
      </Title>

      <Text mt="lg" data-testid="pw-reset-form-text">
        {t("passwordReset.form.instructions")}
      </Text>

      <Box pt="xl">
        <form onSubmit={form.onSubmit((values) => mutate(values))}>
          <LoginTextInput
            {...form.getInputProps("email", { withError: true })}
            key={form.key("email")}
            data-testid="pw-reset-form-email"
          />

          <Button
            type="submit"
            mt="lg"
            data-testid="pw-reset-form-submit"
            disabled={!form.isValid() || isPending}
          >
            {t("submit")}
          </Button>
        </form>
      </Box>
    </PasswordResetCard>
  );
});
